import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { setAppModal } from '../../../redux/action/system'
import { getNeededDateFormat } from '../../../helper'
import { confirmPreOrderThunk, removeOrderFromConfirmPreOrderModalDataThunk } from '../../../redux/thunk/order/confirmPreOrder'
import Currency from '../../Elements/currency'
import CustomModal from '../../Elements/modal/CustomModal'
import { selectCompanyIndex, selectIsNeededZIndex, selectUsername } from '../../../redux/selector/system'
import { ModalInfo, ModalInfoBlock, ModalInfoItem, OrderWrap, PreOrderModalWrapper } from './index.styled'
import { ModalContent, OrderUnpaidContent, OrderUnpaidText, OrderUnpaidWrapper } from '../processingTimeOrder/index.styled'
import MainIcon from '../../../img/svg/main'
import Button from '../../Elements/buttons/Button'
import KeyBindings from '../../Elements/keybindings'
import { logger } from '../../../logger/logger'
import { selectSubscriptionData } from '@liefersoft/pos-ui-core'
import Order from '../../Elements/modal/order/Order'

const EVENT_Z_INDEX = 998
const showPlatformDenyModal = 'FOODORA_PLUGIN'

const ConfirmPreOrderModal = React.memo(() => {
	const { t } = useTranslation()
	const confirmPreOrderModalShow = useSelector(store => store.system.modal?.confirmPreOrderModalShow)
	const isNeededZIndex = useSelector(selectIsNeededZIndex(EVENT_Z_INDEX))
	const order = useSelector(store => store.system.modalData?.confirmPreOrderModalData[0])
	const isProcessingConfirmOrder = useSelector(store => store.order.isProcessingConfirmOrder)
	const dispatch = useDispatch()
	const geoData = order?.customer?.geoData
	const companyIndex = useSelector(selectCompanyIndex)
	const showCancelButton = order?.platformMetaData?.platformType === showPlatformDenyModal
	const payed = order?.payments?.filter(item => item?.method === order?.paymentMethod)[0]?.payed
	const { isDeactivated, willBeEmergencyBlocked } = useSelector(selectSubscriptionData)
	const username = useSelector(selectUsername)
	const modalIsOpen = confirmPreOrderModalShow && (willBeEmergencyBlocked || !isDeactivated)

	useEffect(() => {
		if (confirmPreOrderModalShow) {
			logger.info(`Opening preorder popup for confirming the order from platform orderId=${order.orderId} companyIndex=${companyIndex} path=${window.location.pathname}`)
		}
	}, [confirmPreOrderModalShow]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleConfirm = useCallback(() => {
		const data = { printerMetaData: { userPrintRequest: { userName: username } } }
		dispatch(confirmPreOrderThunk(order?.orderId, data))
	}, [dispatch, order?.orderId, username])

	const handleClose = useCallback(() => {
		dispatch(removeOrderFromConfirmPreOrderModalDataThunk(order?.orderId))
	}, [dispatch, order])

	const handleDeny = useCallback(() => {
		dispatch(setAppModal({ denyPreOrderModalShow: true }))
		handleClose()
	}, [dispatch, handleClose])

	return (
		<>
			<CustomModal isOpen={modalIsOpen}
									 close={handleClose}
									 enableOutsideClick={false}
									 title={t('Modal.confirm_preorder')}
									 zIndex={EVENT_Z_INDEX}
									 size="auto"
									 disableBackButton={true}
									 button={<>
										 {showCancelButton &&
											 <Button name="cancel" icon="clear" text={t('Modal.deny_order')}
															 zIndex={EVENT_Z_INDEX} color="red"
															 onClick={handleDeny}
															 className="back"
															 disabled={isProcessingConfirmOrder} />}
										 <Button name="add" icon="checkmark" text={t('Modal.preorder')} keyButton="F2"
														 onClick={handleConfirm} color="green" zIndex={EVENT_Z_INDEX} tabIndex="909" disabled={isProcessingConfirmOrder} />
									 </>}>
				<PreOrderModalWrapper>
					<ModalContent>
						<ModalInfo>
							<ModalInfoBlock className="small">
								{!payed ?
									<OrderUnpaidWrapper>
										<OrderUnpaidContent>
											<MainIcon icon={order?.paymentMethod} testId={order?.paymentMethod} />
											<OrderUnpaidText data-testid="price"><Currency>{order?.totalPrice}</Currency></OrderUnpaidText>
										</OrderUnpaidContent>
										<OrderUnpaidText className="small">{t('Modal.unpaid_order')}</OrderUnpaidText>
									</OrderUnpaidWrapper> :
									<ModalInfoItem>
										<MainIcon icon={order?.paymentMethod} testId={order?.paymentMethod} />
										<p className="price"><Currency>{order?.totalPrice}</Currency></p>
									</ModalInfoItem>}
							</ModalInfoBlock>
							<ModalInfoBlock className="small">
								<ModalInfoItem>
									<MainIcon icon="restaurant-name" />
									<p className="platform">{order?.platformMetaData?.platformName}</p>
								</ModalInfoItem>
							</ModalInfoBlock>
							<ModalInfoBlock className={geoData ? 'column' : 'lf-col-2'}>
								<ModalInfoItem className="small">
									<MainIcon icon="time" />
									<p className="time">{getNeededDateFormat(order?.preOrder, 'HH:mm') + ' UHR'}</p>
								</ModalInfoItem>
								<ModalInfoItem className="small">
									<MainIcon icon="date" />
									<p className="date">{getNeededDateFormat(order?.preOrder)}</p>
								</ModalInfoItem>
							</ModalInfoBlock>
							{geoData &&
								<ModalInfoBlock className="column">
									<ModalInfoItem className="small left">
										<MainIcon icon="route" />
										<p className="address">{order?.customer?.street} {order?.customer?.streetNumber}, {order?.customer?.postalCode} {order?.customer?.city}</p>
										<p className={geoData?.distance > 0 ? 'distance' : 'lf-hidden'} data-testid="distance">{(geoData?.distance / 1000).toFixed(2)} km</p>
									</ModalInfoItem>
									{geoData?.duration > 0 &&
										<ModalInfoItem className="small">
											<MainIcon icon="time-left" />
											<p className="duration" data-testid="duration">{Math.round(geoData?.duration / 60)} min</p>
										</ModalInfoItem>}
								</ModalInfoBlock>}
						</ModalInfo>
						<OrderWrap>
							<Order order={order} />
						</OrderWrap>
					</ModalContent>
				</PreOrderModalWrapper>
			</CustomModal>

			{(isNeededZIndex && !isProcessingConfirmOrder) &&
				<KeyBindings enter={handleConfirm} />}
		</>
	)
})

ConfirmPreOrderModal.displayName = 'ConfirmPreOrderModal'
export default ConfirmPreOrderModal
